import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { USER_PASSWORD_MIN } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { cn } from '@/shared/lib/cn';
import { Input, Label } from '@/shared/ui';
import { passwordStrength } from './account.api';

/** Password input with a show / hide toggle. */
export function PasswordInput({ id, value, onChange, autoComplete, autoFocus }: { id: string; value: string; onChange: (v: string) => void; autoComplete: string; autoFocus?: boolean }) {
  const { t } = useI18n();
  const [reveal, setReveal] = useState(false);
  return (
    <Input
      id={id}
      type={reveal ? 'text' : 'password'}
      value={value}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      trailing={
        <button type="button" className="rounded p-1 text-muted-foreground hover:text-foreground" aria-label={t('acct.reveal')} onClick={() => setReveal((v) => !v)}>
          {reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      }
    />
  );
}

const STRENGTH_BAR = ['bg-muted', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'];

/** A new password typed twice, with the strength bar. */
export function NewPasswordFields({ password, repeat, onPassword, onRepeat }: { password: string; repeat: string; onPassword: (v: string) => void; onRepeat: (v: string) => void }) {
  const { t } = useI18n();
  const strength = passwordStrength(password);
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="acct-new">{t('acct.newPassword')}</Label>
        <PasswordInput id="acct-new" value={password} onChange={onPassword} autoComplete="new-password" />
        <div className="flex items-center gap-2" aria-live="polite">
          <div className="flex h-1.5 flex-1 gap-1" aria-hidden>
            {[1, 2, 3, 4].map((i) => (
              <span key={i} className={cn('flex-1 rounded-full', strength >= i ? STRENGTH_BAR[strength] : 'bg-muted')} />
            ))}
          </div>
          <span className="w-16 text-right text-[11px] font-semibold text-muted-foreground">{password ? t(`acct.strength.${(['weak', 'weak', 'fair', 'good', 'strong'] as const)[strength]}`) : ''}</span>
        </div>
        <p className="text-xs text-muted-foreground">{t('acct.passwordHint', { min: USER_PASSWORD_MIN })}</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="acct-repeat">{t('acct.repeatPassword')}</Label>
        <PasswordInput id="acct-repeat" value={repeat} onChange={onRepeat} autoComplete="new-password" />
        {repeat && password !== repeat && <p className="text-xs text-danger">{t('acct.mismatch')}</p>}
      </div>
    </>
  );
}
